import { config } from '../config';
import { log } from '../app/logger';
import { describeUnknownError } from '../app/errors';

/**
 * Drives the "thermally loaded" condition §16 U-0 names.
 *
 * **What this is honestly measuring.** iOS exposes no thermal API, so the harness cannot read the
 * device's temperature or throttle state. What it can do is produce sustained multi-core load for
 * a recorded number of seconds and measure commits *during* that load. The recorded output is
 * "commit latency after N seconds of sustained load with M workers", not "commit latency at X
 * degrees" — and the results screen says exactly that.
 *
 * **The stronger measurement is the venue one.** A phone that has been in a pocket, in sunlight,
 * holding a wake lock for forty minutes of a real half is hotter than anything this generator will
 * produce in two minutes. That is why the soak duration can be set to zero: at a venue, mid-match,
 * the right move is to run the thermal condition with no soak at all — the device is already in
 * the state we care about.
 */

const MODULE = 'thermal';

/**
 * Every load generator currently burning CPU, keyed by the run that owns it.
 *
 * The run lock (`runLock.ts`) is what *prevents* overlap. This registry is what makes an overlap
 * that happened anyway **self-identifying** in the data, rather than something an orchestrator has
 * to reconstruct from timestamps across two exported runs — which is how the first false `breached`
 * finding was eventually caught, days later, by hand.
 */
const liveLoads = new Map<string, number>();

/**
 * Heat workers belonging to some **other** run that are burning CPU right now.
 *
 * A run samples this across its own measuring window. Non-zero means its figures include load it
 * did not ask for and does not describe, and `scenario.ts` treats that as an admissibility blocker
 * — the same class of blocker as too few samples, and for the same reason: the number is real but
 * it is not a measurement of the thing under test.
 */
export function foreignHeatWorkers(ownerId: string): number {
  let total = 0;
  for (const [owner, count] of liveLoads) {
    if (owner !== ownerId) total += count;
  }
  return total;
}

/** Test seam — clears the registry between cases. */
export function __resetThermalRegistryForTests(): void {
  liveLoads.clear();
}

export class ThermalLoad {
  private workers: Worker[] = [];
  private startedAt: number | null = null;

  /**
   * @param ownerId the run that owns this load. Registered so any *other* run can see it burning.
   */
  constructor(private readonly ownerId: string) {}

  get workerCount(): number {
    return this.workers.length;
  }

  /**
   * Spawns heat workers. Never throws: a device or browser that refuses to construct workers
   * still gives a usable (if weaker) run, and losing the whole condition to a worker failure would
   * be the wrong trade. The failure is logged and `workerCount` reports the truth.
   */
  start(now: () => number): void {
    if (this.workers.length > 0) return;

    const cores = navigator.hardwareConcurrency ?? 4;
    const target = Math.max(1, Math.min(config.HARNESS_HEAT_WORKERS_MAX, cores));

    for (let i = 0; i < target; i++) {
      try {
        const worker = new Worker(new URL('./heatWorker.ts', import.meta.url), { type: 'module' });
        worker.postMessage({ type: 'start' });
        this.workers.push(worker);
      } catch (cause) {
        log.error(MODULE, 'could not spawn a heat worker; thermal load will be weaker than intended', {
          spawned: this.workers.length,
          target,
          error: describeUnknownError(cause),
        });
        break;
      }
    }

    this.startedAt = now();
    liveLoads.set(this.ownerId, this.workers.length);
    log.info(MODULE, 'thermal load started', {
      owner: this.ownerId,
      workers: this.workers.length,
      cores,
      foreignWorkersAlreadyLive: foreignHeatWorkers(this.ownerId),
    });
  }

  /** @returns seconds of load actually delivered. */
  stop(now: () => number): number {
    const elapsedS = this.startedAt === null ? 0 : (now() - this.startedAt) / 1000;
    for (const worker of this.workers) {
      try {
        worker.postMessage({ type: 'stop' });
        worker.terminate();
      } catch (cause) {
        log.exception(MODULE, 'heat worker did not terminate cleanly', cause);
      }
    }
    this.workers = [];
    this.startedAt = null;
    // Deregistered even if a `terminate()` threw above, so a half-failed stop cannot leave a
    // phantom load flagging every later run as contaminated.
    liveLoads.delete(this.ownerId);
    log.info(MODULE, 'thermal load stopped', { owner: this.ownerId, elapsedS });
    return elapsedS;
  }
}

/**
 * Waits out the soak, reporting progress so the screen is not blank for two minutes at a venue.
 * Resolves early if `shouldStop()` returns true, so the run can be abandoned.
 */
export async function soak(
  seconds: number,
  onTick: (remainingS: number) => void,
  shouldStop: () => boolean,
): Promise<number> {
  const capped = Math.min(seconds, config.HARNESS_THERMAL_SOAK_MAX_S);
  const startedAt = Date.now();

  for (let elapsed = 0; elapsed < capped; elapsed++) {
    if (shouldStop()) break;
    onTick(capped - elapsed);
    await new Promise<void>((resolve) => setTimeout(resolve, 1000));
  }

  return (Date.now() - startedAt) / 1000;
}
